import React from "react";
import { Trash2, X, Users, Info } from "lucide-react";
import { NODE_ICON, NODE_ACCENT } from "./nodeTypes";

/**
 * Configuration panel for whatever is selected on the canvas.
 *
 * Node settings and edge settings live in one component because they are one
 * job — "tell me about this thing I clicked" — and splitting them would mean two
 * panels competing for the same slot.
 */

const L = ({ children }) => (
  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--cares-text-3)",
                  marginBottom: 4, marginTop: 12 }}>{children}</label>
);

const input = {
  width: "100%", padding: "7px 9px", fontSize: 12.5, borderRadius: 7,
  border: "1px solid var(--cares-line-strong)", outline: "none", background: "var(--cares-surface-card)", color: "var(--cares-text-1)",
};

const Hint = ({ children }) => (
  <p style={{ fontSize: 10.5, color: "var(--cares-text-note)", margin: "4px 0 0", lineHeight: 1.45 }}>
    {children}
  </p>
);

/** field / operator / value, with a raw escape hatch for anything more complex. */
function ConditionBuilder({ value, onChange, fields, operators }) {
  const parsed = React.useMemo(() => {
    if (!value) return { field: "", op: "==", val: "" };
    // Only simple three-part conditions round-trip into the dropdowns; anything
    // else (and/or, in [...]) stays in raw mode rather than being mangled.
    const m = String(value).match(/^\s*(\w+)\s*(==|!=|>=|<=|>|<)\s*(.+?)\s*$/);
    if (!m) return null;
    return { field: m[1], op: m[2], val: m[3].replace(/^['"]|['"]$/g, "") };
  }, [value]);

  const [raw, setRaw] = React.useState(parsed === null);
  const field = fields.find((f) => f.field === parsed?.field);

  const emit = (next) => {
    const f = fields.find((x) => x.field === next.field);
    let v = next.val;
    if (f?.type === "boolean") v = String(v) === "true" ? "true" : "false";
    else if (f?.type === "number") v = String(v).trim() || "0";
    else v = `'${String(v).replace(/'/g, "")}'`;
    onChange(next.field ? `${next.field} ${next.op} ${v}` : "");
  };

  if (raw || parsed === null) {
    return (
      <>
        <textarea
          style={{ ...input, fontFamily: "ui-monospace, Menlo, monospace", fontSize: 11.5,
                   minHeight: 60, resize: "vertical" }}
          value={value || ""}
          placeholder="tab_decision in ['APPROVE', 'APPROVE_WITH_CONDITIONS']"
          onChange={(e) => onChange(e.target.value)}
        />
        <Hint>
          Compare a field to a value using ==, !=, &gt;, &gt;=, &lt;, &lt;=, in — combine
          with and / or / not. Leave blank to always take this route.
        </Hint>
        {parsed !== null && (
          <button onClick={() => setRaw(false)}
                  style={{ marginTop: 6, fontSize: 10.5, color: "var(--cares-purple)",
                           background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            Use the simple builder
          </button>
        )}
      </>
    );
  }

  return (
    <>
      <select style={input} value={parsed.field}
              onChange={(e) => emit({ ...parsed, field: e.target.value, val: "" })}>
        <option value="">— always take this route —</option>
        {fields.map((f) => <option key={f.field} value={f.field}>{f.label}</option>)}
      </select>
      {parsed.field && (
        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
          <select style={{ ...input, flex: "0 0 96px" }} value={parsed.op}
                  onChange={(e) => emit({ ...parsed, op: e.target.value })}>
            {operators.filter((o) => o.op !== "in").map((o) =>
              <option key={o.op} value={o.op}>{o.label}</option>)}
          </select>
          {field?.type === "boolean" ? (
            <select style={{ ...input, flex: 1 }} value={parsed.val}
                    onChange={(e) => emit({ ...parsed, val: e.target.value })}>
              <option value="true">yes</option>
              <option value="false">no</option>
            </select>
          ) : field?.options ? (
            <select style={{ ...input, flex: 1 }} value={parsed.val}
                    onChange={(e) => emit({ ...parsed, val: e.target.value })}>
              <option value="">—</option>
              {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input style={{ ...input, flex: 1 }} value={parsed.val}
                   type={field?.type === "number" ? "number" : "text"}
                   onChange={(e) => emit({ ...parsed, val: e.target.value })} />
          )}
        </div>
      )}
      <button onClick={() => setRaw(true)}
              style={{ marginTop: 6, fontSize: 10.5, color: "var(--cares-purple)", background: "none",
                       border: "none", cursor: "pointer", padding: 0 }}>
        Write it by hand instead
      </button>
    </>
  );
}

function AssigneePicker({ value = [], actors, onChange }) {
  const [open, setOpen] = React.useState(false);
  const chosen = new Set(value.map((a) => a.id));
  const groups = [
    ["ROLE", "Roles"], ["GROUP", "Groups"], ["USER", "People"],
  ];

  const toggle = (actor) => {
    onChange(chosen.has(actor.id)
      ? value.filter((a) => a.id !== actor.id)
      : [...value, { id: actor.id, type: actor.type, name: actor.name }]);
  };

  return (
    <>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
        {value.length === 0 && (
          <span style={{ fontSize: 11, color: "var(--cares-amber)" }}>Nobody assigned yet</span>
        )}
        {value.map((a) => (
          <span key={a.id} style={{ display: "inline-flex", alignItems: "center", gap: 4,
                                    background: "color-mix(in srgb, var(--cares-purple) 14%, var(--cares-surface-card))", color: "var(--cares-purple)", fontSize: 10.5,
                                    fontWeight: 600, padding: "3px 6px", borderRadius: 6 }}>
            {a.name}
            <X size={10} style={{ cursor: "pointer" }}
               onClick={() => onChange(value.filter((x) => x.id !== a.id))} />
          </span>
        ))}
      </div>
      <button onClick={() => setOpen((o) => !o)}
              style={{ ...input, cursor: "pointer", textAlign: "left", color: "var(--cares-purple)",
                       fontWeight: 600 }}>
        <Users size={11} style={{ display: "inline", marginRight: 5 }} />
        {open ? "Close directory" : "Assign people, roles or groups"}
      </button>
      {open && (
        <div style={{ marginTop: 6, maxHeight: 210, overflowY: "auto", border: "1px solid var(--cares-line-card)",
                      borderRadius: 8, padding: 6, background: "var(--cares-surface-board)" }}>
          {groups.map(([type, heading]) => {
            const rows = actors.filter((a) => a.type === type);
            if (!rows.length) return null;
            return (
              <div key={type} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 9.5, fontWeight: 700, color: "var(--cares-text-note)",
                              textTransform: "uppercase", letterSpacing: .5, margin: "2px 0 4px" }}>
                  {heading}
                </div>
                {rows.map((a) => (
                  <div key={a.id} onClick={() => toggle(a)}
                       style={{ display: "flex", alignItems: "center", gap: 7, padding: "4px 6px",
                                borderRadius: 6, cursor: "pointer",
                                background: chosen.has(a.id) ? "color-mix(in srgb, var(--cares-purple) 22%, var(--cares-surface-card))" : "transparent" }}>
                    <input type="checkbox" readOnly checked={chosen.has(a.id)} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 11.5, color: "var(--cares-text-1)", fontWeight: 500 }}>{a.name}</div>
                      {a.title && <div style={{ fontSize: 9.5, color: "var(--cares-text-note)" }}>{a.title}</div>}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

export default function Inspector({ selection, palette, actors, domain, readOnly,
                                    onChangeNode, onChangeEdge, onDelete }) {
  if (!selection) {
    return (
      <div style={{ padding: 18, color: "var(--cares-text-note)", fontSize: 12, lineHeight: 1.6 }}>
        <Info size={15} style={{ marginBottom: 6 }} />
        <p style={{ margin: 0 }}>
          Select a step or a connection to configure it. Drag from the panel on the
          left to add steps; drag between the dots on a step to connect them.
        </p>
      </div>
    );
  }

  const fields = (palette?.conditionFields || {})[domain] || [];
  const operators = palette?.operators || [];

  if (selection.kind === "edge") {
    const d = selection.item.data || {};
    const set = (patch) => onChangeEdge(selection.item.id, { ...d, ...patch });
    return (
      <div style={{ padding: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--cares-text-1)" }}>Connection</div>
        <Hint>From “{selection.sourceLabel}” to “{selection.targetLabel}”.</Hint>

        <L>Label on the canvas</L>
        <input style={input} value={d.label || ""} disabled={readOnly}
               placeholder="e.g. Approved"
               onChange={(e) => set({ label: e.target.value })} />

        <L>Take this route when…</L>
        {readOnly ? (
          <div style={{ ...input, background: "var(--cares-surface-board)", fontFamily: "ui-monospace, monospace",
                        fontSize: 11.5 }}>
            {d.condition || "always"}
          </div>
        ) : (
          <ConditionBuilder value={d.condition} fields={fields} operators={operators}
                            onChange={(condition) => set({ condition })} />
        )}

        <label style={{ display: "flex", gap: 7, alignItems: "flex-start", marginTop: 14,
                        fontSize: 11.5, color: "var(--cares-text-2)" }}>
          <input type="checkbox" checked={!!d.isDefault} disabled={readOnly}
                 onChange={(e) => set({ isDefault: e.target.checked })} />
          <span><strong>Otherwise</strong> — used only when no other route matches.</span>
        </label>
        <label style={{ display: "flex", gap: 7, alignItems: "flex-start", marginTop: 8,
                        fontSize: 11.5, color: "var(--cares-text-2)" }}>
          <input type="checkbox" checked={!!d.isPrimary} disabled={readOnly}
                 onChange={(e) => set({ isPrimary: e.target.checked })} />
          <span><strong>Normal course of business</strong> — progress bars follow this
            route rather than a rework loop or a rejection.</span>
        </label>

        {!readOnly && (
          <button onClick={() => onDelete(selection)}
                  style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 6,
                           background: "color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))", color: "var(--cares-orange)", border: "1px solid color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))",
                           borderRadius: 7, padding: "6px 10px", fontSize: 11.5,
                           fontWeight: 600, cursor: "pointer" }}>
            <Trash2 size={12} /> Remove connection
          </button>
        )}
      </div>
    );
  }

  const node = selection.item;
  const d = node.data || {};
  const set = (patch) => onChangeNode(node.id, { ...d, ...patch });
  const Icon = NODE_ICON[node.type] || Info;

  return (
    <div style={{ padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <Icon size={15} style={{ color: NODE_ACCENT[node.type] }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--cares-text-1)",
                       textTransform: "capitalize" }}>{node.type}</span>
      </div>

      <L>Name</L>
      <input style={input} value={d.label || ""} disabled={readOnly}
             onChange={(e) => set({ label: e.target.value })} />

      <L>Description</L>
      <textarea style={{ ...input, minHeight: 54, resize: "vertical" }} disabled={readOnly}
                value={d.description || ""}
                placeholder="What happens here, and what 'done' means."
                onChange={(e) => set({ description: e.target.value })} />

      {node.type === "stage" && (
        <>
          <L>Stage key</L>
          <input style={input} value={d.stageKey || ""} disabled={readOnly}
                 placeholder="TRIAGE"
                 onChange={(e) => set({ stageKey: e.target.value.toUpperCase() })} />
          <Hint>
            Ties this step to records in the database. Records store this exact value
            in their stage column, so changing it on a live workflow strands anything
            already sitting here.
          </Hint>

          <L>Target turnaround (hours)</L>
          <input style={input} type="number" min="0" disabled={readOnly}
                 value={d.slaHours ?? ""}
                 onChange={(e) => set({ slaHours: e.target.value === "" ? null
                                                 : Number(e.target.value) })} />

          <L>Who owns this step</L>
          {readOnly ? (
            <div style={{ fontSize: 11.5, color: "var(--cares-text-3)" }}>
              {(d.assignees || []).map((a) => a.name).join(", ") || "Nobody assigned"}
            </div>
          ) : (
            <AssigneePicker value={d.assignees || []} actors={actors}
                            onChange={(assignees) => set({ assignees })} />
          )}
        </>
      )}

      {node.type === "automation" && (
        <>
          <L>Action</L>
          <select style={input} value={d.action || ""} disabled={readOnly}
                  onChange={(e) => set({ action: e.target.value })}>
            <option value="">— choose an action —</option>
            {(palette?.automationActions || []).map((a) => (
              <option key={a.action} value={a.action}>{a.label}</option>
            ))}
          </select>
          <Hint>Runs without anyone picking it up. Work does not wait here.</Hint>
        </>
      )}

      {node.type === "end" && (
        <>
          <L>Outcome recorded</L>
          <select style={input} value={d.outcome || ""} disabled={readOnly}
                  onChange={(e) => set({ outcome: e.target.value })}>
            {["COMPLETED", "REJECTED", "MERGED", "PROMOTED", "CANCELLED"].map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </>
      )}

      {node.type === "decision" && (
        <Hint>
          Conditions live on the connections out of this step. Click a connection to
          set what sends work down it.
        </Hint>
      )}

      {!readOnly && node.type !== "start" && (
        <button onClick={() => onDelete(selection)}
                style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 6,
                         background: "color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))", color: "var(--cares-orange)", border: "1px solid color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))",
                         borderRadius: 7, padding: "6px 10px", fontSize: 11.5,
                         fontWeight: 600, cursor: "pointer" }}>
          <Trash2 size={12} /> Remove step
        </button>
      )}
    </div>
  );
}
